import React, {useState} from 'react';
import {adminGetMergedAccounts, adminRestoreMergedAccount} from '../../../services/api/admin';
import LoadSpinner from "../../../components/Common/LoadSpinner";
import PaginatedTable from "../../../components/Block/Table/PaginatedTable";
import RestoreMergedAccountModal from "../../../components/Admin/RestoreMergedAccountModal";

const headers = [
    {data: 'ID', classes: 'sl-font-bold'},
    {data: 'From User', classes: 'sl-font-bold'},
    {data: 'To User', classes: 'sl-font-bold'},
    {data: 'Merged By', classes: 'sl-font-bold'},
    {data: 'Created Date', classes: 'sl-font-bold'},
    {data: 'Action', classes: 'sl-font-bold'},
];

const MergedAccountsTable = () => {

    const [mergedAccountsData, setMergedAccountsData] = useState([]);
    const [accountsFetched, setAccountsFetched] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [selectedMergedAccount, setSelectedMergedAccount] = useState(null);

    const handleRestoreClick = (mergedAccount) => {
        setSelectedMergedAccount(mergedAccount);
        setShowRestoreModal(true);
    };

    const handleCloseModal = () => {
        setShowRestoreModal(false);
        setSelectedMergedAccount(null);
    };

    const handleRestore = async (mergedUserId, dryRun) => {
        try {
            const result = await adminRestoreMergedAccount(mergedUserId, dryRun);

            if (result && result.merged_user_id) {
                // If actual restore (not dry run), refresh the list
                if (!dryRun) {
                    setMergedAccountsData([]);
                    setAccountsFetched(false);
                }
                return result;
            } else {
                throw new Error('Restore failed');
            }
        } catch (error) {
            console.error('Error restoring account:', error);
            alert('Error restoring account. Please try again.');
            throw error;
        }
    };

    const fetchMergedAccounts = async () => {
        if (!mergedAccountsData.length && !accountsFetched && !isFetching) {
            try {
                setIsFetching(true);
                const data = await adminGetMergedAccounts();

                if (!data || !Array.isArray(data)) {
                    setIsFetching(false);
                    setAccountsFetched(true);
                    setMergedAccountsData([]);
                    return;
                }

                const transformedData = data.map((item) => {
                    const tempData = {};

                    tempData.id = {
                        data: item.id,
                        classes: "sl-min-w-10"
                    };

                    tempData.from_user = {
                        data: `${item.from_user.first_name} ${item.from_user.last_name} (${item.from_user.email})`,
                        classes: "sl-min-w-148"
                    };

                    tempData.to_user = {
                        data: `${item.to_user.first_name} ${item.to_user.last_name} (${item.to_user.email})`,
                        classes: "sl-min-w-148"
                    };

                    tempData.merged_by = {
                        data: item.user
                            ? `${item.user.first_name} ${item.user.last_name}`
                            : 'N/A',
                        classes: "sl-min-w-10"
                    };

                    tempData.created_at = {
                        data: new Date(item.created_at).toLocaleString(),
                        classes: "sl-min-w-10"
                    };

                    tempData.action = {
                        data: <div>
                            <button
                                className={"sl-btn-sm sl-btn-primary sl-font sl-p-2"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRestoreClick(item);
                                }}
                            >
                                Restore
                            </button>
                        </div>,
                        classes: "sl-min-w-10"
                    };

                    // Store original item data for modal
                    tempData._rawData = item;

                    return tempData;
                });

                setIsFetching(false);
                setAccountsFetched(true);
                setMergedAccountsData(transformedData);

            } catch (e) {
                console.error('Error fetching merged accounts:', e);
                setIsFetching(false);
                setAccountsFetched(true);
            }
        }
    };

    React.useEffect(() => {
            fetchMergedAccounts().then(r => console.log('Merged accounts fetched'));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountsFetched]);

    return (
        <div>
            <main className="sl-main sl-mt-8">
                <div className={"sl-container sl-m-auto sl-my-8"}>
                    <h1 className={`sl-mb-10 sl-text-xl sl-text-gray-900`}>Managing Merged Accounts</h1>
                    <div className="sl-container sl-my-8 sl-flex sl-flex-col sl-gap-3">
                        <div className={"sl-mt-2"}>
                            {accountsFetched ? (
                                mergedAccountsData.length > 0 ? (
                                    <div className="sl-table-container">
                                        <PaginatedTable headers={headers} data={mergedAccountsData}/>
                                    </div>
                                ) : (
                                    <div className="sl-text-center sl-py-8 sl-text-gray-500">
                                        No merged accounts found
                                    </div>
                                )
                            ) : (
                                <div className="sl-self-center sl-mt-3 sl-mb-21">
                                    <LoadSpinner/>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Restore Merged Account Modal */}
            {showRestoreModal && selectedMergedAccount && (
                <RestoreMergedAccountModal
                    isOpen={showRestoreModal}
                    onClose={handleCloseModal}
                    mergedAccount={selectedMergedAccount}
                    onRestore={handleRestore}
                />
            )}
        </div>
    );
};

export default MergedAccountsTable;
